/**
 * Feature switch keys, mirroring TutorPortalAPI's FeatureSwitchKeys and the rows seeded by LeapDb's
 * Seed.FeatureSwitches.sql. A key with no row is off, so a typo here fails closed.
 *
 * The filters.* keys gate each staff list screen's new filter UI (brand filter, new filters, Brand
 * column); see Plans/search-filters-behind-switches.md. Use with FeatureSwitchService.isEnabled or
 * the *appFeatureSwitch directive.
 */
export const FeatureSwitchKeys = {
  orbitBrandBinding: 'orbit.brand-binding',

  /** Email templates catalogue (/email-templates), under New Features. */
  emailTemplatesCatalogue: 'emailtemplates.catalogue',
  /** Email templates page: each preset address's source, its inline editor and the settings link. */
  emailTemplatesPresetAddresses: 'emailtemplates.preset-addresses',
  /** Email templates page: a "Restore this version" button on each wording history entry. */
  emailTemplatesHistoryRestore: 'emailtemplates.history-restore',
  /**
   * Email templates page: the markup editor in place of the plain wording box. Its options are the
   * sub-switches below; the API reports a sub-switch on only while this one is on.
   */
  emailTemplatesRichEditor: 'emailtemplates.rich-editor',
  emailTemplatesRichEditorHighlight: 'emailtemplates.rich-editor.highlight',
  emailTemplatesRichEditorPlaceholders: 'emailtemplates.rich-editor.placeholders',
  emailTemplatesRichEditorAutocomplete: 'emailtemplates.rich-editor.autocomplete',
  emailTemplatesRichEditorLint: 'emailtemplates.rich-editor.lint',
  emailTemplatesRichEditorLineNumbers: 'emailtemplates.rich-editor.line-numbers',
  emailTemplatesRichEditorAutoclose: 'emailtemplates.rich-editor.autoclose',
  emailTemplatesRichEditorFormat: 'emailtemplates.rich-editor.format',
  emailTemplatesRichEditorSearch: 'emailtemplates.rich-editor.search',
  emailTemplatesRichEditorWrap: 'emailtemplates.rich-editor.wrap',

  /** Live activity console (/live-activity), under Admin. */
  liveActivity: 'admin.live-activity',

  /** New Features (/feature-switches): the "Turn features off" panel. It only ever turns features off. */
  featureSwitchesBulkOff: 'featureswitches.bulk-off',
  /** New Features (/feature-switches): each switch's stage (Dark, Rolling out, Adopted) and the stage actions. */
  featureSwitchLifecycle: 'featureswitches.lifecycle',

  /** System Controls (/service-processes): the Settings tab. */
  settingsAdmin: 'settings.admin',
  /** System Controls (/service-processes): the Nudger tab. */
  nudgerRulesEditor: 'nudger.rules-editor',
  /** Email history (/email-history): every email the system tried to send, and whether it went. */
  emailHistory: 'admin.email-history',
  /** Role Features (/role-features): the "Check access" panel, which explains why a user can or cannot use a page. */
  accessCheck: 'admin.access-check',
  /** Role Features: compares the role with the agreed set, shows who last changed each feature, and locks staff-only ones. */
  roleFeaturesAgreedSet: 'admin.role-features-agreed-set',

  /** Tuition Programme Overview header: the booking's Orbit order number and link, for internal staff only. */
  programmeOverviewOrbitOrder: 'programme-overview.orbit-order',
  // Session report delivery setting and the in-person booking's online classroom set-up (39883).
  sessionDeliverySetting: 'sessions.delivery-setting',
  /** Reports screen: Download PDF beside Export to Excel for the native client reports (go-live snag 49). */
  clientReportsPdf: 'reports.client-pdf',
  /** Risk Assessment dialog: Download PDF of the selected assessment (go-live snag 74). */
  riskAssessmentPdf: 'riskassessment.pdf',
  /** Generate screen: Excel or PDF for a batch of native client reports. */
  clientReportsBatchPdf: 'reports.batch-pdf',

  /** Actions (/actions) and the tc-dashboard. */
  filtersActions: 'filters.actions',
  /** Missing submissions (/missing). */
  filtersMissingSubmissions: 'filters.missing-submissions',
  /** Booking search (/booking-search). */
  filtersBookingSearch: 'filters.booking-search',
  /** Session search (/search). */
  filtersSessionSearch: 'filters.session-search',
  /** Session overview (/overview). */
  filtersSessionOverview: 'filters.session-overview',
  /** Learner search. */
  filtersLearnerSearch: 'filters.learner-search',
  /** Client users (/clientadmin). */
  filtersClientUsers: 'filters.client-users',
  /** User maintenance (/user-maintenance). */
  filtersUserMaintenance: 'filters.user-maintenance',
  /** Student feedback dashboard. */
  filtersFeedbackDashboard: 'filters.feedback-dashboard',
  /** Generated reports list (/reports, sent tab). */
  filtersReportsSent: 'filters.reports-sent',
  /** Client reports, View tab (/reports): date presets, remembered filters, Clear all, URL state, filter bar. */
  filtersReportsView: 'filters.reports-view',
  /** Client reports, Generate tab (/reports): date presets, remembered filters, Clear all, URL state, filter bar. */
  filtersReportsGenerate: 'filters.reports-generate',
  /** Reports screen (/reports): one combined page (Download, Send to clients, already sent) plus a History tab. */
  reportsCombinedPage: 'reports.combined-page',
  /** QA report approval (/qa-report-approval): remembers the consultant and report type filters. */
  filtersQaReportApproval: 'filters.qa-report-approval',

  /**
   * Search boxes that suggest matches as they are typed into. The children below are one per screen; the API
   * reports a child on only while this parent is on, so a screen checks just its own child key.
   */
  searchAutocomplete: 'search.autocomplete',
  /** Booking search: Client Name / ID. */
  searchAutocompleteBookingSearch: 'search.autocomplete.booking-search',
  /** Session search: Client, Tutor, Client Referrer. */
  searchAutocompleteSessionSearch: 'search.autocomplete.session-search',
  /** Actions: Client, Tutor, Client Referrer. */
  searchAutocompleteActions: 'search.autocomplete.actions',
  /** Missing submissions: Client, Tutor, Client Referrer. */
  searchAutocompleteMissingSubmissions: 'search.autocomplete.missing-submissions',
  /** Learner search: the client filter. */
  searchAutocompleteLearnerSearch: 'search.autocomplete.learner-search',
  /** Client users: the search box. */
  searchAutocompleteClientUsers: 'search.autocomplete.client-users',
  /** User maintenance: Name or email. */
  searchAutocompleteUserMaintenance: 'search.autocomplete.user-maintenance',
  /** New Features edit dialog: the named-user search. */
  searchAutocompleteFeatureSwitchUsers: 'search.autocomplete.feature-switch-users',
  /** Reports, Sent tab: Recipient. */
  searchAutocompleteReportsSent: 'search.autocomplete.reports-sent',
  /**
   * Who can see a booking: the Can see choice on each Client Users link (Only bookings they are named on, or Every booking at this client), with the bookings a person is named on,
   * and Who can see this booking on booking setup. Also needs the BookingSetupView role feature.
   */
  clientUsersBookingGrants: 'clientusers.booking-grants',
} as const;

export type FeatureSwitchKey = typeof FeatureSwitchKeys[keyof typeof FeatureSwitchKeys];
